<template>
  <div :class="$style.view">
    <PreviewChatMessageModal @click-backdrop="closeModal">
      <PreviewChatMessageModalView
        v-if="preview"
        :message="preview.preview.message"
        :selections="selections"
        :maybe-other-user-names="preview.maybeOtherUserNames"
        @close="closeModal"
        @confirm="confirmSendMessage"
      />
    </PreviewChatMessageModal>
    <div
      :class="[$style.view__main, $style.message]"
      v-if="roomId === undefined"
    >
      <OkanekoChatFp :class="$style.message__image" />
      <p :class="$style.message__body">チャットが選択されていません</p>
    </div>
    <template v-else-if="match && chatRoom">
      <ChatPageHeader
        :userName="match.user.nickname"
        :class="$style.view__header"
        :status="chatRoom.status"
        :roomId="roomId"
        :memos="chatRoom.memos"
        :isUpdatingMemos="isUpdatingMemos"
        :is-team-administrator-member="isTeamAdministratorMember"
        :normalMessageChatReplyDisabled="normalMessageChatReplyDisabled"
        @createMemo="createMemo"
        @deleteMemo="deleteMemo"
        @updateStatus="updateStatus"
        @updatedRole="$emit('onUpdatedRole')"
        @updateNormalMessageChatReplyMatchSetting="
          updateNormalMessageChatReplyMatchSetting
        "
      />
      <div :class="$style.view__wrapper">
        <div :class="[$style.view__main, $style.main]">
          <div
            :class="$style.main__chat"
            ref="messageView"
            @scroll="throttledMessageViewOnScrollHandler"
          >
            <ChatMessageList
              :messages="messages"
              :match="match"
              :isUserTyping="isUserTyping"
              @change="scrollMessageViewToBottom"
            />
          </div>
          <div :class="[$style.main__actions, $style.actions]">
            <div :class="$style.actions__sender">
              <ChatPageAction
                :key="match.id"
                :isLineBlocked="
                  chatRoom.user?.lineActivationUser?.isBlocked || false
                "
                v-model:text="message"
                v-model:selections="selections"
                v-model:pickedTimeSlots="pickedTimeSlots"
                v-model:fallbackUrl="fallbackUrl"
                v-model:sendAsStaff="sendAsStaff"
                :isSending="isSending"
                :showSendAsStaffButton="showSendAsStaffButton"
                @typing="handleTyping"
                @send="onSendMessage"
              />
            </div>
          </div>
        </div>
        <div :class="$style.view__info">
          <UserProfile
            :match="match"
            :diagnosisSummary="diagnosisSummary"
            :karte="karte"
            @openDiagnosisModal="onOpenDiagnosisModal"
            @openUserConcernAndDiagnosisModal="
              onOpenUserConcernAndDiagnosisModal
            "
          >
            <template #footer>
              <div :class="$style.subactions">
                <!-- LINE 連携のユーザーには、評価を依頼ボタンを表示しない（現状 LINE 上で評価ができないので） -->
                <ReviewButton
                  v-if="chatRoom.user && !chatRoom.user.lineActivationUser"
                  @click="openReviewModal"
                  :class="$style.button"
                  :disabled="!reviewRequestable.isRequestable"
                />
              </div>
            </template>
          </UserProfile>
        </div>
      </div>
    </template>
    <div v-else-if="matchError" :class="[$style.view__main, $style.message]">
      <OkanekoError :class="$style.message__image" />
      <p :class="$style.message__body">チャットの取得に失敗しました</p>
    </div>

    <ReviewModal v-if="match != null">
      <ReviewRequestModal
        :user-nickname="match.user.nickname"
        @confirm="clickRequestReview"
        @cancel="closeReviewModal"
      />
    </ReviewModal>
  </div>
</template>

<script setup lang="ts">
import { watch, ref, computed } from 'vue'
import { ChatRoomStatus } from '@/types/schema.generated'
import { captureException } from '@sentry/vue'

// components
import UserProfile from '@/components/organisms/UserProfile/UserProfile.vue'
import ChatMessageList from '@/components/organisms/ChatMessageList/ChatMessageList.vue'
import OkanekoChatFp from '@/assets/images/okaneko-chatfp.svg?component'
import OkanekoError from '@/assets/images/okaneko-error.svg?component'
import ReviewButton from './ReviewButton.vue'
import ChatPageAction from './ChatPageAction.vue'
import ChatPageHeader from './ChatPageHeader.vue'
import ReviewRequestModal from './ReviewRequestModal.vue'

// hooks
import { useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import {
  useOwnChatRoom,
  useMessageSocket,
  useMessageViewScrollingControl,
} from './hooks'
import { useFetchMessages } from '@/hooks/useFetchMessages'

// app types
import type { RoomID } from '@/types/app'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { useAlert } from '@/libs/alertPortal'

import {
  useAddChatRoomMemoMutation,
  useDeleteChatRoomMemoMutation,
  useUpdateStatusMutation,
  type ChatPageOwnViewPreviewNormalChatMessageQuery,
  ChatPageOwnViewPreviewNormalChatMessageDocument,
  type ChatPageOwnViewPreviewQuestionChatMessageQuery,
  ChatPageOwnViewPreviewQuestionChatMessageDocument,
  useChatPageOwnViewReviewLazyQuery,
} from './ChatPageOwnView.generated'
import { empty } from '@/components/organisms/TimeSlotPickerModal/useTimeSlot'
import { usePersistentMessageForm } from './hooks/usePersistentMessageForm'
import { ApolloError } from '@apollo/client/core'
import { useApolloClient } from '@vue/apollo-composable'
import { useModal } from '@/hooks/useModal'
import PreviewChatMessageModalView from './PreviewChatMessageModalView.vue'
import { useLogger } from '@/logger/logger'
import { useEntranceGUIDParam } from '@/features/entrance/useEntranceGUIDParam'
import { useMaybeRoutes } from '@/features/router/route'

const emit = defineEmits<{
  (event: 'update:loading', value: boolean): void
  (event: 'onUpdateChatRoom', ids: string[]): void
  (event: 'onUpdatedRole'): void
}>()

const NUMBER_OF_MESSAGE_ON_ONE_FETCH = 40
const SCROLLING_THROTTLE_INTERVAL = 300

const logger = useLogger()

const router = useRouter()

const maybeRoute = useMaybeRoutes(['ownChat', 'assignedChat'])

const roomId = computed((): Maybe<RoomID> => {
  if (maybeRoute.value != undefined) {
    return Number.parseInt(maybeRoute.value.params.roomId)
  }
  return undefined
})

const { entranceGUID } = useEntranceGUIDParam({ must: true })

const isDisabledToScrollToBottom = ref<boolean>(false)

const pickedTimeSlots = ref(empty())
const fallbackUrl = ref<string>()

watch(
  roomId,
  async value => {
    try {
      if (value === undefined) {
        // no id route
        return
      }
      const _roomId = Number(value)
      if (Number.isNaN(_roomId)) {
        // eslint-disable-next-line fp/no-mutating-methods
        await router.push({ name: 'errorBadRequest' })
        return
      }
      isDisabledToScrollToBottom.value = false
    } catch (error) {
      captureException(error)

      // eslint-disable-next-line fp/no-mutating-methods
      await router.push({ name: 'errorNotFound' })
    }
  },
  { immediate: true }
)

const {
  chatRoom,
  match,
  matchRefetch,
  matchError,
  diagnosisSummary,
  karte,
  loading: chatRoomLoading,
  refetch,
  viewer,
} = useOwnChatRoom(roomId)

const {
  fetchNextMessages,
  fetchPreviousMessages,
  appendMessage,
  messages,
  loading: messageLoading,
} = useFetchMessages(
  match,
  NUMBER_OF_MESSAGE_ON_ONE_FETCH,
  isDisabledToScrollToBottom,
  entranceGUID.value
)

const loading = computed(() => chatRoomLoading.value || messageLoading.value)

watch(loading, () => {
  emit('update:loading', loading.value)
})

const {
  messageView,
  scrollMessageViewToBottom,
  throttledMessageViewOnScrollHandler,
} = useMessageViewScrollingControl(
  isDisabledToScrollToBottom,
  SCROLLING_THROTTLE_INTERVAL,
  async () => {
    try {
      await fetchPreviousMessages()
    } catch (error) {
      if (!isClientError(error)) {
        captureException(error)
      }
    }
  }
)

const {
  result: reviewResult,
  load: reviewLoad,
  refetch: reviewRefetch,
} = useChatPageOwnViewReviewLazyQuery(
  computed(() => ({
    userId: String(match.value?.user.id),
  }))
)

watch(
  () => match.value,
  async () => {
    if (match.value) {
      try {
        await reviewLoad()
      } catch (e) {
        // not found の場合は何もしない
        if (e instanceof ApolloError) {
          if (e.graphQLErrors.some(e => e.extensions?.code === 'NOT_FOUND')) {
            return
          }
        }
        captureException(e)
      }
    }
  },
  { immediate: true }
)

const reviewRequestable = computed(() => {
  if (reviewResult.value) {
    return {
      isRequestable: false,
      updateHook: 'never' as const,
    }
  }
  if (messages.value.length < 3) {
    return { isRequestable: false, updateHook: 'addMessage' as const }
  }
  return {
    isRequestable: true,
    updateHook: 'never' as const,
  }
})

const { handleTyping, handleRequestReview, handleSendMessage, isUserTyping } =
  useMessageSocket({
    matchRef: match,
    reviewRequestabilityRef: reviewRequestable,
    isDisabledToScrollToBottomRef: isDisabledToScrollToBottom,
    appendMessageHandler: appendMessage,
    onReconnected: async () => {
      try {
        await fetchNextMessages(true)
      } catch (error) {
        if (!isClientError(error)) {
          captureException(error)
        }
      }
    },
    fallbackRefreshHandler: async () => {
      try {
        await fetchNextMessages(true)
      } catch (error) {
        if (!isClientError(error)) {
          captureException(error)
        }
      }
    },
    entranceGUID: entranceGUID.value,
  })

const alert = useAlert()

// UI上の送信処理の状態
const processingReviewRequest = ref(false)

const { openReviewModal, closeReviewModal, ReviewModal } = useModal('review')

const clickRequestReview = async () => {
  if (processingReviewRequest.value) {
    return
  }
  processingReviewRequest.value = true
  try {
    await handleRequestReview()
    await reviewRefetch()
  } catch (error) {
    alert.error(
      'レビューのリクエストに失敗しました。時間を空けて再度お試しください。'
    )
    if (!isClientError(error)) {
      captureException(error)
    }
  } finally {
    processingReviewRequest.value = false
  }

  closeReviewModal()
}

const {
  PreviewChatMessageModal,
  openPreviewChatMessageModal,
  closePreviewChatMessageModal,
} = useModal('previewChatMessage')

const { message, selections } = usePersistentMessageForm(roomId)

const preview =
  ref<
    ChatPageOwnViewPreviewQuestionChatMessageQuery['previewQuestionChatMessage']
  >()

const apollo = useApolloClient()

const fetchPreview = async () => {
  if (roomId?.value === undefined) {
    return
  }

  if (selections.value.length > 0) {
    const { data } =
      await apollo.client.query<ChatPageOwnViewPreviewQuestionChatMessageQuery>(
        {
          query: ChatPageOwnViewPreviewQuestionChatMessageDocument,
          variables: {
            input: {
              chatRoomId: roomId.value.toString(),
              message: message.value,
              answerSelections: selections.value,
              sendAsStaff: sendAsStaff.value,
            },
          },
        }
      )

    if (data.previewQuestionChatMessage.maybeOtherUserNames.length > 0) {
      preview.value = {
        preview: {
          message: data.previewQuestionChatMessage.preview.message,
        },
        maybeOtherUserNames:
          data.previewQuestionChatMessage.maybeOtherUserNames,
      }
    }

    return
  }

  const { data } =
    await apollo.client.query<ChatPageOwnViewPreviewNormalChatMessageQuery>({
      query: ChatPageOwnViewPreviewNormalChatMessageDocument,
      variables: {
        input: {
          chatRoomId: roomId.value.toString(),
          message: message.value,
          sendAsStaff: sendAsStaff.value,
          attachments: [], // previewには不要なので空配列でOK
        },
      },
    })

  if (data.previewNormalChatMessage.maybeOtherUserNames.length > 0) {
    preview.value = {
      preview: {
        message: data.previewNormalChatMessage.preview.message,
      },
      maybeOtherUserNames: data.previewNormalChatMessage.maybeOtherUserNames,
    }

    return
  }
}

const closeModal = () => {
  preview.value = undefined
  closePreviewChatMessageModal()
}

const onSendMessage = async () => {
  if (roomId?.value === undefined) {
    return
  }

  if (viewer.value === undefined) {
    return
  }

  if (!viewer.value.is400FOA) {
    sendMessage()
    return
  }

  await fetchPreview()

  if (
    preview.value?.maybeOtherUserNames &&
    preview.value?.maybeOtherUserNames.length > 0
  ) {
    openPreviewChatMessageModal()
    return
  }
  sendMessage()
}

const confirmSendMessage = () => {
  closeModal()
  sendMessage()
}

const isSending = ref<boolean>(false)
const sendAsStaff = ref(false)
const showSendAsStaffButton = computed(() => {
  if (!viewer.value) {
    return false
  }
  return viewer.value.hasLineActivationProvider
})

const sendMessage = async () => {
  if (match.value === undefined) {
    return
  }

  isSending.value = true

  try {
    await handleSendMessage(
      message.value,
      selections.value,
      sendAsStaff.value,
      pickedTimeSlots.value,
      !!fallbackUrl.value ? fallbackUrl.value : undefined
    )

    fallbackUrl.value = undefined
    pickedTimeSlots.value = empty()
    message.value = ''
    selections.value = []
  } catch (err) {
    console.error(err)
    captureException(err)
    if (
      err instanceof ApolloError &&
      err.graphQLErrors.some(
        e => e.extensions && e.extensions.code === 'INVALID_ARGUMENT'
      )
    ) {
      alert.error(
        '不正な値が送信されました。内容を確認の上再度お試しください。'
      )
      return
    }
    alert.error(
      'メッセージの送信に失敗しました。再度時間をおいてお試しください。'
    )
  } finally {
    isSending.value = false
  }
}

const onOpenDiagnosisModal = async () => {
  if (match.value === undefined) {
    return
  }

  if (maybeRoute.value != undefined) {
    await logger.ltlPeople.event.openDiagnosisModal(logger.buildMeta(), {
      data: {
        path: maybeRoute.value.fullPath,
        userId: match.value.user.id.toString(),
      },
    })
  }
}

const onOpenUserConcernAndDiagnosisModal = async () => {
  if (match.value === undefined || karte.value === undefined) {
    return
  }
  await logger.ltlPeople.event.showKarteDetailModal(logger.buildMeta(), {
    data: {
      userId: match.value.user.id,
      karteId: Number(karte.value.id),
    },
  })
}

const computedHead = computed(() => {
  const user = match.value?.user
  const title =
    user !== undefined ? `${user.nickname || 'ゲスト'} | チャット` : 'チャット'
  return {
    title,
  }
})

useHead(computedHead)

const { mutate: addChatRoomMemo, loading: loadingAddChatRoomMemo } =
  useAddChatRoomMemoMutation({})

const { mutate: deleteChatRoomMemo, loading: loadingDeleteChatRoomMemo } =
  useDeleteChatRoomMemoMutation({})

const isTeamAdministratorMember = computed(() => {
  if (!viewer.value) {
    return false
  }
  return viewer.value.staff.role !== 'MEMBER'
})

const isUpdatingMemos = computed(
  () => loadingAddChatRoomMemo.value || loadingDeleteChatRoomMemo.value
)

const createMemo = async (content: string) => {
  if (roomId.value == null || isUpdatingMemos.value) {
    return
  }

  try {
    await addChatRoomMemo({
      chatRoomID: roomId.value.toString(),
      content,
    })

    // レスポンスによるキャッシュ更新がされないときがあるので手動で refetch する
    // いずれはデフォルト cache オフにしたい
    refetch()
    emit('onUpdateChatRoom', [roomId.value.toString()])
  } catch (e) {
    captureException(e)
    alert.error('メモの追加に失敗しました。再度時間をおいてお試しください。')
  }
}

const deleteMemo = async (id: string) => {
  if (roomId.value == null || isUpdatingMemos.value) {
    return
  }

  try {
    await deleteChatRoomMemo({
      id,
    })

    // レスポンスによるキャッシュ更新がされないときがあるので手動で refetch する
    // いずれはデフォルト cache オフにしたい
    refetch()
    emit('onUpdateChatRoom', [roomId.value.toString()])
  } catch (e) {
    captureException(e)
    alert.error('メモの削除に失敗しました。再度時間をおいてお試しください。')
  }
}

const { mutate: _updateStatus } = useUpdateStatusMutation({})

const updateStatus = async (status: ChatRoomStatus) => {
  if (!roomId.value) {
    return
  }

  try {
    await _updateStatus({
      id: roomId.value.toString(),
      status: status,
    })

    // レスポンスによるキャッシュ更新がされないときがあるので手動で refetch する
    // いずれはデフォルト cache オフにしたい
    refetch()
    emit('onUpdateChatRoom', [roomId.value.toString()])
  } catch (e) {
    captureException(e)
    alert.error(
      'ステータスの更新に失敗しました。再度時間をおいてお試しください。'
    )
  }
}

const normalMessageChatReplyDisabled = computed(() => {
  if (!match.value) {
    throw new Error('match not found')
  }
  const disabledUntil =
    match.value.normalMessageChatReplyMatchSetting.disabledUntil
  if (!disabledUntil) {
    return false
  }
  return Date.now() < disabledUntil.getTime()
})

const updateNormalMessageChatReplyMatchSetting = () => {
  // この関数が実行されているということは roomId が存在するということなので、
  // 型ガードとして roomId が存在することを保証する
  if (!roomId.value) {
    return
  }

  // header 内の状態表示で使用している normalMessageChatReplyDisabled
  // を更新するために match を再取得する
  matchRefetch()
  emit('onUpdateChatRoom', [roomId.value.toString()])
}
</script>

<style lang="scss" module>
$border: 1px solid $color-border-low-emphasis;

.view {
  display: flex;
  flex-direction: column;

  &__header {
    border-bottom: 1px solid $color-border-low-emphasis;
  }

  &__wrapper {
    display: flex;
    flex: 1;
    overflow-y: hidden;
  }

  &__main {
    flex: 1;
  }

  &__info {
    overflow: auto;
    width: 260px;
    border-left: 1px solid $color-border-low-emphasis;
    background-color: $color-surface-primary;
  }
}

.message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &__image {
    width: 200px;
  }

  &__body {
    color: $color-text-and-object-low-emphasis;
    font-size: 18px;
    font-weight: bold;
    text-align: center;

    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.main {
  display: grid;
  height: 100%;
  grid-template: 1fr auto / 100%;

  &__chat {
    overflow: auto;
  }

  &__actions {
    overflow: scroll;
    max-height: 500px;
    border-top: 1px solid $color-border-low-emphasis;
  }
}

.actions {
  &__sender {
    padding: 24px 40px;
    background-color: $color-surface-primary;
    isolation: isolate;

    &:nth-child(n + 2) {
      border-top: $border;
    }
  }
}

.button {
  display: flex;
  width: 100%;
}

.subactions {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &:nth-child(n + 2) {
    margin-top: 40px;
  }
}
</style>
